import { Language } from '../valueobject/language.ts';
import { useState } from 'react';
import { i18n } from '@lingui/core';

interface UseLanguageViewModelProps {
  onLanguageChanged?: (language: Language) => void;
}

export const LANGUAGE_STORAGE_KEY = 'selectedLanguage';

export interface LanguageViewModel {
  loadLanguage: () => void;
  currentLanguage: Language;
  changeLanguage: (language: Language) => void;
}

/**
 * A hook that provides a language view model for managing application language preferences.
 * It includes functionality to load and change the current language as well as storing the language setting.
 */
export default function useLanguageViewModel({ onLanguageChanged }: UseLanguageViewModelProps): LanguageViewModel {
  const supportedLanguageList = Object.values(Language);
  const fallbackLanguage = Language.EN;

  const [currentLanguage, setCurrentLanguage] = useState<Language>(() => {
    const storedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    const browserLanguage = findSupportedLanguage(window.navigator.language);
    return findSupportedLanguage(storedLanguage) || browserLanguage || fallbackLanguage;
  });

  function loadLanguage() {
    changeLanguage(currentLanguage);
  }

  function changeLanguage(language: Language) {
    i18n.activate(language);
    setCurrentLanguage(language);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    document.documentElement.lang = language;
    onLanguageChanged?.(language);
  }

  function findSupportedLanguage(requestedLanguage: string | null | undefined): Language | null {
    if (requestedLanguage == null) {
      return null;
    }

    const requestedLanguageLowerCase = requestedLanguage.toLowerCase();

    for (const supportedLanguage of supportedLanguageList) {
      if (supportedLanguage.toLowerCase() === requestedLanguageLowerCase) {
        return supportedLanguage;
      }
    }

    if (requestedLanguageLowerCase.includes('-')) {
      const baseLanguagePart = requestedLanguageLowerCase.split('-')[0];

      for (const supportedLanguage of supportedLanguageList) {
        if (supportedLanguage.toLowerCase() === baseLanguagePart) {
          return supportedLanguage;
        }
      }
    }

    return null;
  }

  return {
    loadLanguage,
    currentLanguage,
    changeLanguage,
  };
}
